import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { emailApi, labelsApi, contactsApi, messengerApi } from "../api/api";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";
import EmailList from "../components/email/EmailList";
import EmailViewer from "../components/email/EmailViewer";
import ComposeModal from "../components/email/ComposeModal";
import PaymentRequiredModal from "../components/payment/PaymentRequiredModal";
import UserStorageSettings from "../components/storage/UserStorageSettings";
import Messenger from "../components/Messenger";
import { toast } from "sonner";
import { GripVertical, Pencil } from "lucide-react";

const FOLDER_NAMES = {
  inbox: 'Inbox',
  drafts: 'Drafts', 
  sent: 'Sent',
  starred: 'Starred',
  archive: 'Archive',
  spam: 'Spam',
  trash: 'Trash'
};

export default function EmailPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, logout, getTrialDaysLeft, isTrialExpired, refreshUser } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();

  const [emails, setEmails] = useState([]);
  const [folderCounts, setFolderCounts] = useState({});
  const [labels, setLabels] = useState([]);
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [activeEmail, setActiveEmail] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);

  // Refresh user data on mount to get latest subscription status
  useEffect(() => {
    refreshUser();
  }, []);

  // Check trial status whenever user data changes
  useEffect(() => {
    if (user) {
      const daysLeft = getTrialDaysLeft();
      setTrialDaysLeft(daysLeft);
      
      // Show modal if trial expired (but not for admins)
      if (isTrialExpired()) {
        setShowPaymentModal(true);
      } else {
        setShowPaymentModal(false);
      }
    }
  }, [user, getTrialDaysLeft, isTrialExpired]);

  const [storageSettingsOpen, setStorageSettingsOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [forwardEmail, setForwardEmail] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [messengerOpen, setMessengerOpen] = useState(false);
  const [messengerUnreadCount, setMessengerUnreadCount] = useState(0);

  // Resizable panel state
  const [listWidth, setListWidth] = useState(() => {
    // Load from localStorage or default to 400px
    const saved = localStorage.getItem('emailListWidth');
    return saved ? parseInt(saved, 10) : 400;
  });
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const MIN_LIST_WIDTH = 280;
  const MAX_LIST_WIDTH = 1200; // Allow up to ~70% of typical screen

  // Fetch messenger unread count
  const fetchMessengerUnread = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await messengerApi.getUnreadCount();
      setMessengerUnreadCount(data.count);
    } catch (err) {
      console.error('Failed to fetch messenger unread:', err);
    }
  }, [isAuthenticated]);

  // Poll messenger unread count
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchMessengerUnread();
    const interval = setInterval(fetchMessengerUnread, 30000); // every 30 seconds
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchMessengerUnread]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle panel resize drag
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const newWidth = e.clientX - containerRect.left;
    
    // Allow up to 70% of container width
    const maxDynamicWidth = Math.min(containerRect.width * 0.7, MAX_LIST_WIDTH);
    
    // Clamp to min/max
    const clampedWidth = Math.min(Math.max(newWidth, MIN_LIST_WIDTH), maxDynamicWidth);
    setListWidth(clampedWidth);
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      // Save to localStorage
      localStorage.setItem('emailListWidth', listWidth.toString());
    }
  }, [isDragging, listWidth]);

  // Touch handlers for mobile
  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;
    
    const touch = e.touches[0];
    const containerRect = containerRef.current.getBoundingClientRect();
    const newWidth = touch.clientX - containerRect.left;
    
    // Allow up to 70% of container width
    const maxDynamicWidth = Math.min(containerRect.width * 0.7, MAX_LIST_WIDTH);
    
    const clampedWidth = Math.min(Math.max(newWidth, MIN_LIST_WIDTH), maxDynamicWidth);
    setListWidth(clampedWidth);
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      localStorage.setItem('emailListWidth', listWidth.toString());
    }
  }, [isDragging, listWidth]);

  // Add global mouse/touch listeners when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Fetch emails when folder changes
  const fetchEmails = useCallback(async (isRefresh = false) => {
    if (!isAuthenticated) return;
    
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const data = await emailApi.getEmails(activeFolder, searchQuery);
      setEmails(data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch emails:', err);
      if (err.response?.status === 401) {
        logout();
        navigate('/login');
      } else if (!isRefresh) {
        toast.error('Failed to load emails');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activeFolder, searchQuery, isAuthenticated, logout, navigate]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes
    
    const intervalId = setInterval(() => {
      fetchEmails(true);
      fetchFolderCounts();
    }, REFRESH_INTERVAL);
    
    return () => clearInterval(intervalId);
  }, [isAuthenticated, fetchEmails]);

  // Manual check mail handler
  const handleCheckMail = useCallback(async () => {
    if (refreshing) return;
    
    setRefreshing(true);
    try {
      await Promise.all([
        fetchEmails(true),
        fetchFolderCounts()
      ]);
      toast.success('Mail checked');
    } catch (err) {
      console.error('Failed to check mail:', err);
    } finally {
      setRefreshing(false);
    }
  }, [refreshing, fetchEmails]);

  // Fetch folder counts
  const fetchFolderCounts = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const counts = await emailApi.getFolderCounts();
      setFolderCounts(counts);
    } catch (err) {
      console.error('Failed to fetch folder counts:', err);
    }
  }, [isAuthenticated]);

  // Fetch labels
  const fetchLabels = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const data = await labelsApi.getAll();
      setLabels(data);
    } catch (err) {
      console.error('Failed to fetch labels:', err);
    }
  }, [isAuthenticated]);

  // Custom folders state
  const [customFolders, setCustomFolders] = useState([]);

  // Fetch custom folders
  const fetchCustomFolders = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const data = await emailApi.getCustomFolders();
      setCustomFolders(data);
    } catch (err) {
      console.error('Failed to fetch custom folders:', err);
    }
  }, [isAuthenticated]);

  // Create custom folder
  const handleCreateFolder = async (name, color) => {
    try {
      await emailApi.createCustomFolder(name, color);
      toast.success('Folder created');
      fetchCustomFolders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create folder');
      throw err;
    }
  };

  // Delete custom folder
  const handleDeleteFolder = async (folderId) => {
    try {
      await emailApi.deleteCustomFolder(folderId);
      toast.success('Folder deleted');
      fetchCustomFolders();
      if (activeFolder === `custom_${folderId}`) {
        setActiveFolder('inbox');
      }
    } catch (err) {
      toast.error('Failed to delete folder');
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (isAuthenticated) {
      fetchEmails();
      fetchFolderCounts();
      fetchLabels();
      fetchCustomFolders();
    }
  }, [isAuthenticated, fetchEmails, fetchFolderCounts, fetchLabels, fetchCustomFolders]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isAuthenticated) {
        fetchEmails();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, isAuthenticated, fetchEmails]);

  const currentFolderName = FOLDER_NAMES[activeFolder] || 'Inbox';

  // Email actions
  const handleSelectEmail = (emailId) => {
    setSelectedEmails(prev =>
      prev.includes(emailId)
        ? prev.filter(id => id !== emailId)
        : [...prev, emailId]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedEmails(emails.map(e => e.id));
    } else {
      setSelectedEmails([]);
    }
  };

  const handleOpenEmail = async (email) => {
    setActiveEmail(email);
    
    // Mark as read if not already
    if (!email.read) {
      try {
        await emailApi.updateEmail(email.id, { read: true });
        setEmails(prev =>
          prev.map(e => (e.id === email.id ? { ...e, read: true } : e))
        );
        fetchFolderCounts();
      } catch (err) {
        console.error('Failed to mark as read:', err);
      }
    }
    
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const handleToggleStar = async (emailId) => {
    const email = emails.find(e => e.id === emailId);
    if (!email) return;

    const newStarred = !email.starred;
    
    // Optimistic update
    setEmails(prev =>
      prev.map(e => (e.id === emailId ? { ...e, starred: newStarred } : e))
    );
    if (activeEmail?.id === emailId) {
      setActiveEmail(prev => ({ ...prev, starred: newStarred }));
    }

    try {
      await emailApi.updateEmail(emailId, { starred: newStarred });
      fetchFolderCounts();
    } catch (err) {
      console.error('Failed to toggle star:', err);
      // Revert on error
      setEmails(prev =>
        prev.map(e => (e.id === emailId ? { ...e, starred: !newStarred } : e))
      );
    }
  };

  const handleDelete = async (emailId) => {
    try {
      await emailApi.deleteEmail(emailId);
      setEmails(prev => prev.filter(e => e.id !== emailId));
      if (activeEmail?.id === emailId) {
        setActiveEmail(null);
      }
      setSelectedEmails(prev => prev.filter(id => id !== emailId));
      fetchFolderCounts();
      toast.success('Email moved to trash');
    } catch (err) {
      console.error('Failed to delete email:', err);
      toast.error('Failed to delete email');
    }
  };

  const handleArchive = async (emailId) => {
    try {
      await emailApi.updateEmail(emailId, { folder: 'archive' });
      setEmails(prev => prev.filter(e => e.id !== emailId));
      if (activeEmail?.id === emailId) {
        setActiveEmail(null);
      }
      setSelectedEmails(prev => prev.filter(id => id !== emailId));
      fetchFolderCounts();
      toast.success('Email archived');
    } catch (err) {
      console.error('Failed to archive email:', err);
      toast.error('Failed to archive email');
    }
  };

  // Mark as unread
  const handleMarkUnread = async (emailId) => {
    const email = emails.find(e => e.id === emailId);
    if (!email) return;
    
    const newReadStatus = !email.read;
    
    // Optimistic update
    setEmails(prev =>
      prev.map(e => (e.id === emailId ? { ...e, read: newReadStatus } : e))
    );
    if (activeEmail?.id === emailId) {
      setActiveEmail(prev => ({ ...prev, read: newReadStatus }));
    }

    try {
      await emailApi.updateEmail(emailId, { read: newReadStatus });
      fetchFolderCounts();
      toast.success(newReadStatus ? 'Marked as read' : 'Marked as unread');
    } catch (err) {
      console.error('Failed to toggle read status:', err);
      // Revert on error
      setEmails(prev =>
        prev.map(e => (e.id === emailId ? { ...e, read: !newReadStatus } : e))
      );
      toast.error('Failed to update email');
    }
  };

  // Move to folder (for drag & drop and bulk actions)
  const handleMoveToFolder = async (emailIds, targetFolder) => {
    if (!emailIds || emailIds.length === 0) return;
    
    try {
      // Update all emails
      await Promise.all(emailIds.map(id => 
        emailApi.updateEmail(id, { folder: targetFolder })
      ));
      
      // Remove from current view if not in target folder
      if (activeFolder !== targetFolder) {
        setEmails(prev => prev.filter(e => !emailIds.includes(e.id)));
        if (activeEmail && emailIds.includes(activeEmail.id)) {
          setActiveEmail(null);
        }
      }
      
      setSelectedEmails([]);
      fetchFolderCounts();
      
      const folderNames = {
        inbox: 'Inbox',
        drafts: 'Drafts',
        sent: 'Sent',
        archive: 'Archive',
        spam: 'Spam',
        trash: 'Trash'
      };
      
      toast.success(`Moved ${emailIds.length} email${emailIds.length > 1 ? 's' : ''} to ${folderNames[targetFolder] || targetFolder}`);
    } catch (err) {
      console.error('Failed to move emails:', err);
      toast.error('Failed to move emails');
    }
  };

  // Compose actions
  const handleCompose = () => {
    setReplyTo(null);
    setForwardEmail(null);
    setComposeOpen(true);
  };

  const handleReply = (email, replyAll = false) => {
    setReplyTo(email);
    setForwardEmail(null);
    setComposeOpen(true);
  };

  const handleForward = (email) => {
    setForwardEmail(email);
    setReplyTo(null);
    setComposeOpen(true);
  };

  const handleSendEmail = async (emailData) => {
    try {
      await emailApi.sendEmail({
        to: [emailData.to],
        cc: emailData.cc ? emailData.cc.split(',').map(e => e.trim()).filter(Boolean) : [],
        bcc: emailData.bcc ? emailData.bcc.split(',').map(e => e.trim()).filter(Boolean) : [],
        subject: emailData.subject,
        body: emailData.body,
        attachments: emailData.attachments || []
      });
      fetchEmails();
      fetchFolderCounts();
      toast.success('Email sent successfully', {
        description: 'End-to-end encrypted'
      });
    } catch (err) {
      console.error('Failed to send email:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to send email';
      toast.error(errorMsg);
    }
  };

  const handleSaveDraft = async (emailData) => {
    try {
      await emailApi.saveDraft({
        to: emailData.to ? [emailData.to] : [],
        cc: emailData.cc ? emailData.cc.split(',').map(e => e.trim()).filter(Boolean) : [],
        bcc: emailData.bcc ? emailData.bcc.split(',').map(e => e.trim()).filter(Boolean) : [],
        subject: emailData.subject || '(no subject)',
        body: emailData.body || '',
        attachments: emailData.attachments || []
      });
      fetchFolderCounts();
      toast.success('Draft saved');
    } catch (err) {
      console.error('Failed to save draft:', err);
      toast.error('Failed to save draft');
    }
  };

  // Transform user data for components
  const currentUser = useMemo(() => ({
    name: user?.display_name || 'User',
    email: user?.email || '',
    avatar: user?.display_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U',
    plan: user?.role === 'admin' ? 'Administrator' : 'MyPatrionMail User',
    storage: {
      used: ((user?.storage_used || 0) / (1024 * 1024 * 1024)).toFixed(2),
      total: ((user?.storage_limit || 15 * 1024 * 1024 * 1024) / (1024 * 1024 * 1024)).toFixed(0),
      unit: 'GB'
    }
  }), [user]);

  // Transform folder counts for sidebar
  const folders = useMemo(() => [
    { id: 'inbox', name: 'Inbox', icon: 'Inbox', count: folderCounts.inbox || 0 },
    { id: 'drafts', name: 'Drafts', icon: 'FileEdit', count: folderCounts.drafts || 0 },
    { id: 'sent', name: 'Sent', icon: 'Send', count: 0 },
    { id: 'starred', name: 'Starred', icon: 'Star', count: folderCounts.starred || 0 },
    { id: 'archive', name: 'Archive', icon: 'Archive', count: 0 },
    { id: 'spam', name: 'Spam', icon: 'AlertTriangle', count: folderCounts.spam || 0 },
    { id: 'trash', name: 'Trash', icon: 'Trash2', count: folderCounts.trash || 0 },
  ], [folderCounts]);

  if (authLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#16162a]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6D4AFF]"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'bg-[#16162a]' : 'bg-[#f8f9fc]'}`}>
      {/* Header */}
      <Header
        onMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        isDarkMode={isDarkMode}
        onToggleTheme={toggleTheme}
        user={currentUser}
        onLogout={logout}
        isAdmin={user?.role === 'admin'}
        onCheckMail={handleCheckMail}
        refreshing={refreshing}
        lastRefresh={lastRefresh}
        onToggleMessenger={() => setMessengerOpen(!messengerOpen)}
        messengerUnreadCount={messengerUnreadCount}
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Mobile Overlay */}
        {mobileMenuOpen && isMobile && (
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar - Hidden on mobile when menu closed */}
        {(!isMobile || mobileMenuOpen) && (
          <Sidebar
            activeFolder={activeFolder}
            onFolderChange={(folder) => {
              setActiveFolder(folder);
              setActiveEmail(null);
              setSelectedEmails([]);
              if (isMobile) setMobileMenuOpen(false);
            }}
            onCompose={handleCompose}
            collapsed={isMobile ? false : sidebarCollapsed}
            onToggleCollapse={() => {
              if (isMobile) {
                setMobileMenuOpen(false);
              } else {
                setSidebarCollapsed(!sidebarCollapsed);
              }
            }}
            isMobile={isMobile}
            folders={folders}
            labels={labels}
            customFolders={customFolders}
            onCreateFolder={handleCreateFolder}
            onDeleteFolder={handleDeleteFolder}
            user={currentUser}
            onOpenStorageSettings={() => setStorageSettingsOpen(true)}
            onDropEmails={handleMoveToFolder}
          />
        )}

        {/* Main Content - Resizable Split Panel */}
        <div ref={containerRef} className="flex flex-1 overflow-hidden relative min-w-0">
          {/* Email List - Resizable on desktop, full width on mobile */}
          <div 
            className={`${activeEmail && isMobile ? 'hidden' : 'flex'} flex-col border-r ${isDarkMode ? 'border-[#2d2d44]' : 'border-gray-200'} overflow-hidden`}
            style={{ 
              width: isMobile ? '100%' : `${Math.min(listWidth, containerRef.current?.offsetWidth * 0.7 || listWidth)}px`,
              minWidth: isMobile ? '100%' : `${MIN_LIST_WIDTH}px`,
              maxWidth: isMobile ? '100%' : '70%',
              flexShrink: 0
            }}
            data-testid="email-list-panel"
          >
            <EmailList
              emails={emails}
              selectedEmails={selectedEmails}
              activeEmail={activeEmail}
              onSelectEmail={handleSelectEmail}
              onSelectAll={handleSelectAll}
              onOpenEmail={handleOpenEmail}
              onToggleStar={handleToggleStar}
              onDelete={handleDelete}
              onArchive={handleArchive}
              onMarkUnread={handleMarkUnread}
              onMoveToFolder={handleMoveToFolder}
              folderName={currentFolderName}
              loading={loading}
              labels={labels}
            />
          </div>

          {/* Resize Handle - Desktop only */}
          {!isMobile && (
            <div
              className={`w-1.5 flex-shrink-0 cursor-col-resize group relative z-10 ${
                isDragging 
                  ? (isDarkMode ? 'bg-[#6D4AFF]' : 'bg-[#6D4AFF]') 
                  : (isDarkMode ? 'bg-[#2d2d44] hover:bg-[#6D4AFF]/50' : 'bg-gray-200 hover:bg-[#6D4AFF]/50')
              } transition-colors duration-150`}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              data-testid="resize-handle"
            >
              {/* Grip indicator */}
              <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity ${isDragging ? 'opacity-100' : ''}`}>
                <GripVertical className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
            </div>
          )}

          {/* Email Viewer */}
          <div className={`${!activeEmail && isMobile ? 'hidden' : 'flex'} flex-1 flex-col min-w-0 overflow-hidden`}>
            <EmailViewer
              email={activeEmail}
              onBack={() => setActiveEmail(null)}
              onReply={handleReply}
              onForward={handleForward}
              onToggleStar={handleToggleStar}
              onDelete={handleDelete}
              onArchive={handleArchive}
              labels={labels}
              currentUserEmail={user?.email}
            />
          </div>
        </div>
      </div>

      {/* Compose Modal */}
      <ComposeModal
        isOpen={composeOpen}
        onClose={() => setComposeOpen(false)}
        replyTo={replyTo}
        forwardEmail={forwardEmail}
        onSend={handleSendEmail}
        onSaveDraft={handleSaveDraft}
        userEmail={user?.email}
      />

      {/* Payment Required Modal */}
      <PaymentRequiredModal
        isOpen={showPaymentModal}
        trialDaysLeft={trialDaysLeft}
        onClose={() => setShowPaymentModal(false)}
      />

      {/* Storage Settings Modal */}
      <UserStorageSettings
        isOpen={storageSettingsOpen}
        onClose={() => setStorageSettingsOpen(false)}
      />

      {/* Messenger Panel */}
      <Messenger
        isOpen={messengerOpen}
        onClose={() => setMessengerOpen(false)}
        onToggle={() => setMessengerOpen(!messengerOpen)}
      />

      {/* Mobile Floating Compose Button (FAB) */}
      {isMobile && !activeEmail && !composeOpen && (
        <button
          onClick={handleCompose}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#6D4AFF] hover:bg-[#5B3FD9] shadow-lg shadow-[#6D4AFF]/30 flex items-center justify-center transition-all duration-200 active:scale-95"
          data-testid="mobile-compose-fab"
        >
          <Pencil className="w-6 h-6 text-white" />
        </button>
      )}
    </div>
  );
}
